/**
 * Dialog "Nova proposta" no detalhe do lead (PR-M1-01c-i + PR-M1-01c-iii.a+.b).
 *
 * Escopo atualizado (iii.a+.b):
 *   - Select de "Tipo de proposta" no topo (saas | consulting | saas_consulting)
 *   - Defaults inteligentes por tipo ao trocar no select
 *   - Campos condicionais via subcomponentes (ProposalSaasFields /
 *     ProposalConsultingFields)
 *   - IA continua gerando só `notes` (PR-M1-01c-i hotfix)
 *   - Modal de notas auto-abre após IA (PR-M1-01c-i UX)
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  toast,
} from '@kmgestao/ui';

import {
  createProposalInputSchema,
  useCreateProposal,
  useSuggestProposalDraft,
  useUpdateProposalDraft,
  type CreateProposalInput,
  type LeadProposalSummary,
  type ProposalType,
} from '../../../features/proposals/index.js';

import { NewProposalAiAssistant } from './NewProposalAiAssistant.js';
import { NewProposalFormFields } from './NewProposalFormFields.js';
import { NewProposalNotesModal } from './NewProposalNotesModal.js';

export interface NewProposalDialogProps {
  leadId: string;
  leadName: string;
  trigger?: ReactNode;
  onCreated?: (proposalId: string) => void;
  /** Proposta existente a editar. Quando presente, o dialog entra em
   * **modo edição**: submit chama `useUpdateProposalDraft` em vez de
   * `useCreateProposal`. Só aceita editar rascunhos (status=draft);
   * caller deve filtrar isso. */
  existingProposal?: LeadProposalSummary | null;
  /** Controle externo do open (usado pelo LeadProposalsSection quando
   * abre modo edição a partir de um card). Quando ambos são fornecidos,
   * `open` controla a visibilidade externamente. */
  open?: boolean;
  onOpenChange?: (next: boolean) => void;
}

/** Converte uma proposta existente no shape de input do form — usado
 * quando o dialog entra em modo edição. Cada variante do discriminated
 * union precisa dos seus campos preenchidos corretamente. */
function proposalToFormInput(proposal: LeadProposalSummary): CreateProposalInput {
  // Calcula `valid_until_days` aproximado a partir da data atual até
  // o vencimento. Se o user editar e não alterar esse campo, a data
  // absoluta muda (reset de prazo a partir de hoje — comportamento
  // esperado pra rascunho sendo revisado).
  const now = Date.now();
  const validUntilMs = new Date(proposal.valid_until).getTime();
  const daysUntilValid = Math.max(1, Math.ceil((validUntilMs - now) / 86_400_000));

  const common = {
    lead_id: proposal.id, // será ignorado — lead_id real vem do dialog externo
    trial_days: proposal.trial_days ?? 14,
    valid_until_days: daysUntilValid,
    notes: proposal.notes ?? '',
    // Pré-preenche campos de contexto da IA ao reabrir proposta em edição
    // (PR-M1-01c-ii.m) — Alexandre pediu pra não perder o que digitou.
    ai_context: proposal.ai_context ?? null,
    ai_highlights: proposal.ai_highlights ?? null,
  };

  if (proposal.proposal_type === 'saas') {
    return {
      proposal_type: 'saas',
      ...common,
      plan_key: proposal.plan_key ?? 'pro',
      price_mode: proposal.discount_percent !== null ? 'discount' : 'custom',
      discount_percent: proposal.discount_percent,
      custom_price_brl: proposal.custom_price_brl,
    };
  }
  if (proposal.proposal_type === 'consulting') {
    return {
      proposal_type: 'consulting',
      ...common,
      trial_days: 0,
      consulting_installments_count: proposal.consulting_installments_count ?? 6,
      consulting_installment_brl: proposal.consulting_installment_brl ?? 3000,
      consulting_duration_months: proposal.consulting_duration_months ?? 6,
      consulting_weekly_hours: proposal.consulting_weekly_hours ?? 5,
      consulting_extra_hour_brl: proposal.consulting_extra_hour_brl ?? 350,
      consulting_scope_notes: proposal.consulting_scope_notes,
      consulting_modality: proposal.consulting_modality ?? 'hibrido',
      // Mantido pra back-compat: valor legado é exibido no ViewProposalModal,
      // mas o form não coleta mais (PR-M1-01c-ii.d). Novos rascunhos entram com null.
      consulting_payment_method: proposal.consulting_payment_method ?? null,
    };
  }
  return {
    proposal_type: 'saas_consulting',
    ...common,
    plan_key: proposal.plan_key ?? 'pro',
    price_mode: proposal.discount_percent !== null ? 'discount' : 'custom',
    discount_percent: proposal.discount_percent,
    custom_price_brl: proposal.custom_price_brl,
    consulting_installments_count: proposal.consulting_installments_count ?? 6,
    consulting_installment_brl: proposal.consulting_installment_brl ?? 3000,
    consulting_duration_months: proposal.consulting_duration_months ?? 6,
    consulting_weekly_hours: proposal.consulting_weekly_hours ?? 5,
    consulting_extra_hour_brl: proposal.consulting_extra_hour_brl ?? 350,
    consulting_scope_notes: proposal.consulting_scope_notes,
    consulting_modality: proposal.consulting_modality ?? 'hibrido',
    consulting_payment_method: proposal.consulting_payment_method ?? 'asaas_boleto',
    saas_free_during_consulting: proposal.saas_free_during_consulting,
  };
}

/** Defaults por tipo — aplicados na inicialização do form e ao
 * trocar o tipo no select. Valores escolhidos refletem o modelo
 * comercial atual da Karla Martins Consultoria (6 × R$ 3.000, 6 meses,
 * 5h/sem, R$ 350/h extra) + defaults SaaS conservadores (Pro + 0% desconto). */
function defaultsForType(leadId: string, type: ProposalType): CreateProposalInput {
  const common = {
    lead_id: leadId,
    trial_days: 14,
    valid_until_days: 14,
    notes: '',
    ai_context: null,
    ai_highlights: null,
  };
  const saasFields = {
    plan_key: 'pro' as const,
    price_mode: 'discount' as const,
    discount_percent: 0,
    custom_price_brl: null,
  };
  const consultingFields = {
    consulting_installments_count: 6,
    consulting_installment_brl: 3000,
    consulting_duration_months: 6,
    consulting_weekly_hours: 5,
    consulting_extra_hour_brl: 350,
    consulting_scope_notes: null,
    consulting_modality: 'hibrido' as const,
    // null por default — form não coleta mais este campo (PR-M1-01c-ii.d,
    // cobrança via Asaas UNDEFINED deixa cliente escolher).
    consulting_payment_method: null as null,
  };

  if (type === 'saas') {
    return { proposal_type: 'saas', ...common, ...saasFields };
  }
  if (type === 'consulting') {
    // Consultoria pura: trial não se aplica (define 0)
    return { proposal_type: 'consulting', ...common, trial_days: 0, ...consultingFields };
  }
  return {
    proposal_type: 'saas_consulting',
    ...common,
    ...saasFields,
    ...consultingFields,
    saas_free_during_consulting: true,
  };
}

export function NewProposalDialog({
  leadId,
  leadName,
  trigger,
  onCreated,
  existingProposal,
  open: openProp,
  onOpenChange: onOpenChangeProp,
}: NewProposalDialogProps) {
  const isEdit = Boolean(existingProposal);
  const [internalOpen, setInternalOpen] = useState(false);
  // Modo controlado (vindo do pai quando é edição) ou não controlado
  // (quando renderizado como trigger standalone).
  const open = openProp ?? internalOpen;
  const setOpen = onOpenChangeProp ?? setInternalOpen;

  const [aiError, setAiError] = useState<string | null>(null);
  const [notesModalOpen, setNotesModalOpen] = useState(false);

  const suggestDraft = useSuggestProposalDraft();
  const createProposal = useCreateProposal();
  const updateProposal = useUpdateProposalDraft();

  const initialDefaults = existingProposal
    ? { ...proposalToFormInput(existingProposal), lead_id: leadId }
    : defaultsForType(leadId, 'saas');

  const form = useForm<CreateProposalInput>({
    resolver: zodResolver(createProposalInputSchema),
    defaultValues: initialDefaults,
  });

  const { handleSubmit, setValue, formState, reset, getValues } = form;

  const resetAll = () => {
    reset(
      existingProposal
        ? { ...proposalToFormInput(existingProposal), lead_id: leadId }
        : defaultsForType(leadId, 'saas'),
    );
    setAiError(null);
    setNotesModalOpen(false);
  };

  /** Ao trocar tipo no select, reinicializa os campos daquela variante
   * preservando o que faz sentido (notas + lead). Evita Zod rejeitar
   * submit com campos faltando da variante anterior. */
  const handleTypeChange = (next: ProposalType) => {
    const currentNotes = getValues('notes');
    const fresh = defaultsForType(leadId, next);
    reset({ ...fresh, notes: currentNotes });
  };

  const onGenerate = (input: {
    context: string;
    highlights: string;
    includeQuestionnaireAnswers: boolean;
  }) => {
    setAiError(null);
    const current = getValues();
    // IA agora recebe o contexto **completo** da proposta (PR-M1-01c-ii.l,
    // 2026-04-24): antes só tinha SaaS e por isso ignorava consultoria
    // no combo. Preencher todos os campos + deixar null os que não se
    // aplicam ao tipo. A Regra #2 do prompt proíbe citar valores,
    // então a IA usa esses campos só pra contexto estruturado.
    const hasSaas = current.proposal_type === 'saas' || current.proposal_type === 'saas_consulting';
    const hasConsulting =
      current.proposal_type === 'consulting' || current.proposal_type === 'saas_consulting';
    const isCombo = current.proposal_type === 'saas_consulting';
    const consultingTotal =
      hasConsulting &&
      typeof current.consulting_installment_brl === 'number' &&
      typeof current.consulting_installments_count === 'number'
        ? current.consulting_installment_brl * current.consulting_installments_count
        : null;
    const prefs = {
      proposal_type: current.proposal_type,
      plan_key: hasSaas ? current.plan_key : null,
      price_mode: hasSaas ? current.price_mode : null,
      discount_percent: hasSaas ? current.discount_percent : null,
      custom_price_brl: hasSaas ? current.custom_price_brl : null,
      trial_days: current.trial_days,
      valid_until_days: current.valid_until_days,
      consulting_total_brl: consultingTotal,
      consulting_installments_count: hasConsulting ? current.consulting_installments_count : null,
      consulting_installment_brl: hasConsulting ? current.consulting_installment_brl : null,
      consulting_duration_months: hasConsulting ? current.consulting_duration_months : null,
      consulting_weekly_hours: hasConsulting ? current.consulting_weekly_hours : null,
      consulting_extra_hour_brl: hasConsulting ? current.consulting_extra_hour_brl : null,
      consulting_modality: hasConsulting ? current.consulting_modality : null,
      saas_free_during_consulting: isCombo ? current.saas_free_during_consulting : null,
    };
    suggestDraft.mutate(
      {
        lead_id: leadId,
        context: input.context,
        highlights: input.highlights,
        include_questionnaire_answers: input.includeQuestionnaireAnswers,
        user_preferences: prefs,
      },
      {
        onSuccess: (res) => {
          // Apenas notas — campos estruturais são do usuário (fix PR #122).
          setValue('notes', res.draft.notes_pt_br, { shouldDirty: true });
          setNotesModalOpen(true);
          toast.success('Rascunho gerado — revise antes de criar.');
        },
        onError: (err) => {
          const msg = (err as Error).message || 'Falha ao gerar rascunho.';
          // Mensagem mais amigável quando é timeout (o hook já tentou
          // 3 vezes com backoff antes de chegar aqui).
          const isTimeout = /timeout|gateway|502|504|fetch/i.test(msg);
          const friendly = isTimeout
            ? 'O serviço de IA está lento no momento. Tentamos 3 vezes sem sucesso. Você pode clicar em "Gerar rascunho" de novo em alguns instantes, ou preencher manualmente abaixo.'
            : `${msg} Você pode preencher manualmente abaixo.`;
          setAiError(friendly);
          toast.error(isTimeout ? 'IA indisponível (timeout)' : 'Falha ao gerar rascunho', {
            description: msg.slice(0, 150),
          });
        },
      },
    );
  };

  const onSubmit = handleSubmit((values) => {
    if (isEdit && existingProposal) {
      // Modo edição: UPDATE em colunas específicas. valid_until recalcula
      // a partir de now() + valid_until_days (reset de prazo pra hoje).
      const validUntil = new Date(Date.now() + values.valid_until_days * 86_400_000).toISOString();
      // Monta patch com campos editáveis por tipo.
      const common = {
        proposal_type: values.proposal_type,
        trial_days: values.trial_days,
        valid_until: validUntil,
        notes: values.notes,
        // Persiste contexto da IA pra reaproveitar em re-edições
        // (PR-M1-01c-ii.m).
        ai_context: values.ai_context ?? null,
        ai_highlights: values.ai_highlights ?? null,
      };
      const saasPatch =
        values.proposal_type === 'saas' || values.proposal_type === 'saas_consulting'
          ? {
              plan_key: values.plan_key, // handler ignora (é resolvido para plan_id via lookup no create;
              // na edição usamos direto o plan_id atual — ou ajustamos em PR futuro
              discount_percent: values.price_mode === 'discount' ? values.discount_percent : null,
              custom_price_brl: values.price_mode === 'custom' ? values.custom_price_brl : null,
            }
          : {};
      const consultingPatch =
        values.proposal_type === 'consulting' || values.proposal_type === 'saas_consulting'
          ? {
              consulting_installments_count: values.consulting_installments_count,
              consulting_installment_brl: values.consulting_installment_brl,
              consulting_duration_months: values.consulting_duration_months,
              consulting_weekly_hours: values.consulting_weekly_hours,
              consulting_extra_hour_brl: values.consulting_extra_hour_brl,
              consulting_scope_notes: values.consulting_scope_notes,
              consulting_modality: values.consulting_modality,
              consulting_payment_method: values.consulting_payment_method,
            }
          : {};
      const comboPatch =
        values.proposal_type === 'saas_consulting'
          ? { saas_free_during_consulting: values.saas_free_during_consulting }
          : {};
      // `plan_key` no patch acima não é uma coluna — precisamos remover
      // antes do UPDATE. A coluna real é `plan_id` (uuid). Por ora não
      // editamos plano no modo edição (complica o lookup — seria fácil
      // mas esse PR foca em visualizar/editar notas/valores do mesmo
      // tipo). Ajuste futuro: aceitar mudança de plano no edit.
      const { plan_key: _drop, ...saasPatchClean } = saasPatch;
      updateProposal.mutate(
        {
          id: existingProposal.id,
          patch: { ...common, ...saasPatchClean, ...consultingPatch, ...comboPatch },
          lead_id: leadId,
        },
        {
          onSuccess: () => {
            toast.success('Rascunho atualizado.');
            resetAll();
            setOpen(false);
          },
          onError: (err) => {
            toast.error('Falha ao atualizar', { description: (err as Error).message });
          },
        },
      );
      return;
    }
    createProposal.mutate(values, {
      onSuccess: (res) => {
        toast.success('Rascunho de proposta criado.');
        resetAll();
        setOpen(false);
        onCreated?.(res.proposal_id);
      },
      onError: (err) => {
        toast.error('Falha ao criar proposta', { description: (err as Error).message });
      },
    });
  });

  const busy = formState.isSubmitting || createProposal.isPending || updateProposal.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) resetAll();
      }}
    >
      {/* Trigger só aparece em modo criação (controlado pelo pai no
          modo edição via prop `open`). */}
      {isEdit ? null : (
        <DialogTrigger asChild>
          {trigger ?? (
            <Button size="lg" variant="outline">
              Criar proposta
            </Button>
          )}
        </DialogTrigger>
      )}

      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? `Editar rascunho da proposta` : `Nova proposta para ${leadName}`}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Ajuste o que precisar. O prazo de validade é recalculado a partir de hoje ao salvar — se ficou 14 dias, vale até daqui 14 dias.'
              : 'Escolha o tipo, configure os campos e gere o rascunho com IA ou preencha manualmente. Ao criar, o rascunho fica disponível para revisar e enviar ao lead num próximo passo.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} noValidate className="mt-6 space-y-4">
          <NewProposalFormFields
            form={form}
            disabled={busy}
            onExpandNotes={() => setNotesModalOpen(true)}
            onProposalTypeChange={handleTypeChange}
            aiAssistantSlot={
              <NewProposalAiAssistant
                isGenerating={suggestDraft.isPending}
                errorMessage={aiError}
                context={
                  (form.watch('ai_context' as never) as unknown as string | null | undefined) ?? ''
                }
                onContextChange={(next) =>
                  setValue('ai_context' as never, (next || null) as never, { shouldDirty: true })
                }
                highlights={
                  (form.watch('ai_highlights' as never) as unknown as string | null | undefined) ??
                  ''
                }
                onHighlightsChange={(next) =>
                  setValue('ai_highlights' as never, (next || null) as never, {
                    shouldDirty: true,
                  })
                }
                onGenerate={onGenerate}
              />
            }
          />

          <DialogFooter className="mt-6 gap-2">
            <DialogClose asChild>
              <Button type="button" variant="outline" size="lg" disabled={busy}>
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" size="lg" disabled={busy}>
              {isEdit
                ? updateProposal.isPending
                  ? 'Salvando…'
                  : 'Salvar alterações'
                : createProposal.isPending
                  ? 'Salvando…'
                  : 'Criar rascunho'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      <NewProposalNotesModal
        open={notesModalOpen}
        onOpenChange={setNotesModalOpen}
        value={form.watch('notes')}
        onApply={(nextValue) =>
          setValue('notes', nextValue, { shouldDirty: true, shouldValidate: true })
        }
      />
    </Dialog>
  );
}
